import React from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { NotificationBell } from '../Notifications';
import { ThemeToggle } from '../ThemeToggle';
import { useAuth } from '../../hooks/useAuth';
import { AnalysesChip } from './AnalysesChip';

interface AppBarProps {
  onToggleSidebar: () => void;
  onLogout: () => void;
  selectedMenuLabel: string;
  isMobile?: boolean;
  /**
   * External portal, new IA (billingEnabled): show the "N analyses" balance chip. Layout decides —
   * the chip (and its billing fetch) never mounts for staff/partner or the dark External shell.
   */
  showAnalysesChip?: boolean;
}

export const AppBar: React.FC<AppBarProps> = ({
  onToggleSidebar,
  onLogout,
  selectedMenuLabel,
  isMobile = false,
  showAnalysesChip = false,
}) => {
  // Notifications are internal-staff only — the External/Partner boundary 403s the endpoints,
  // so the bell would always be an empty dead-end for portal-confined roles.
  const { isExternal, isPartner } = useAuth();
  const showNotifications = !isExternal && !isPartner;

  return (
    <MuiAppBar
      position="static"
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 1200,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        <IconButton
          edge="start"
          onClick={onToggleSidebar}
          sx={{
            mr: { xs: 1, sm: 2 },
            color: 'primary.main',
            display: 'inline-flex'
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{
            flex: 1,
            color: 'primary.main',
            fontWeight: 600,
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {selectedMenuLabel}
        </Typography>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 }
        }}>
          {showAnalysesChip && <AnalysesChip />}
          <Box sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            <ThemeToggle />
          </Box>
          {showNotifications && (
            <Box sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
              <NotificationBell />
            </Box>
          )}
          <Button
            onClick={onLogout}
            startIcon={!isMobile ? <LogoutIcon /> : undefined}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              minWidth: { xs: 'auto', sm: '64px' },
              px: { xs: 1, sm: 2 },
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: (theme) => `rgba(${theme.vars.palette.primary.mainChannel} / 0.1)`,
              },
            }}
          >
            {isMobile ? <LogoutIcon fontSize="small" /> : 'Logout'}
          </Button>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};
